import { MovementEntity } from '../../domain/entities/Movement';
import type { RegisterMovementDTO } from '../dtos/movement/RegisterMovementDTO';
import type { MovementResponseDTO } from '../dtos/movement/MovementResponseDTO';
import { MovementTypes, MovementStatusTypes } from '../../domain/shared/types';
import { InsufficientStockError, ValidationError } from '../../domain/shared/errors';
import type { IStockService } from '../../domain/services/interfaces';

interface IMovementSaver {
  save(entity: MovementEntity): Promise<void>;
}

export interface RegisterMovementUseCase {
  execute(dto: RegisterMovementDTO): Promise<MovementResponseDTO>;
}

export default class RegisterMovementUseCaseImpl implements RegisterMovementUseCase {
  constructor(
    private readonly movementRepository: IMovementSaver,
    private readonly stockService?: IStockService,
  ) {}

  async execute(dto: RegisterMovementDTO): Promise<MovementResponseDTO> {
    if (dto.quantity <= 0) {
      throw new ValidationError('Quantity must be greater than zero');
    }

    if (!Number.isInteger(dto.quantity)) {
      throw new ValidationError('Quantity must be a whole number');
    }

    if (
      dto.unitCost !== undefined &&
      dto.unitPrice !== undefined &&
      dto.unitPrice < dto.unitCost
    ) {
      throw new ValidationError('Unit price cannot be less than unit cost');
    }

    const movementType = this.mapMovementType(dto.type);

    // Validate stock before OUT/TRANSFER
    if (this.stockService && (movementType === MovementTypes.OUT || movementType === MovementTypes.TRANSFER)) {
      const available = await this.stockService.getAvailableStock(dto.productId, dto.warehouseId);
      if (available.value < dto.quantity) {
        throw new InsufficientStockError(dto.productId, dto.quantity, available.value, dto.warehouseId);
      }
    }

    const movement = MovementEntity.create({
      movementType,
      productId: dto.productId,
      warehouseId: dto.warehouseId,
      quantity: { value: dto.quantity, unit: 'units' },
      unitCost: dto.unitCost !== undefined
        ? { amount: dto.unitCost, currency: 'USD' }
        : { amount: 0, currency: 'USD' },
      unitPrice: dto.unitPrice !== undefined
        ? { amount: dto.unitPrice, currency: 'USD' }
        : undefined,
      status: MovementStatusTypes.COMPLETED,
      initiatedBy: 'system',
      completedBy: 'system',
      reference: dto.reference,
      reason: dto.reason,
      notes: dto.notes,
      destinationWarehouseId: dto.destinationWarehouseId,
    });

    await this.movementRepository.save(movement);

    return {
      id: movement._id,
      movementType: movement.movementType,
      productId: movement.productId,
      warehouseId: movement.warehouseId,
      quantity: movement.quantity,
      unitCost: movement.unitCost,
      unitPrice: movement.unitPrice,
      status: movement.status,
      reference: movement.reference,
      reason: movement.reason,
      notes: movement.notes,
      destinationWarehouseId: movement.destinationWarehouseId,
      createdAt: movement.createdAt,
      updatedAt: movement.updatedAt,
    };
  }

  private mapMovementType(type: 'in' | 'out' | 'transfer' | 'adjust') {
    const mapping = {
      'in': MovementTypes.IN,
      'out': MovementTypes.OUT,
      'transfer': MovementTypes.TRANSFER,
      'adjust': MovementTypes.ADJUSTMENT,
    } as const;
    return mapping[type];
  }
}
